import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  keyframes,
} from '@chakra-ui/react';
import { InputProps } from '@copilotkit/react-ui';
import { ArrowUp, MessageCircle } from 'lucide-react';
import { FC, KeyboardEvent, useCallback, useRef, useState } from 'react';
import { GradientIcon } from '~/common/components/GradientIcon';

const glowPulse = keyframes`
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
`;

export interface CopilotChatFloatingInputProps extends InputProps {
  autoFocus?: boolean;
}

export const CopilotChatFloatingInput: FC<CopilotChatFloatingInputProps> = ({
  autoFocus,
  ...props
}) => {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    if (!value.trim()) return;
    void props.onSend(value);
    setValue('');
  }, [value, props]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <Flex bottom={0} p={4} position="sticky" w="100%">
      {/* Outer glow container */}
      <Box
        _before={{
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          opacity: isFocused ? 1 : 0,
          animation: isFocused
            ? `${glowPulse} 3s ease-in-out infinite`
            : 'none',
          transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none',
        }}
        border={isFocused ? '2px solid' : '1px solid'}
        borderColor={isFocused ? 'purple.gradient.end' : 'purple.gradient.end'}
        borderRadius="full"
        boxShadow={
          isFocused
            ? '0px 0px 30px 2px rgba(117, 111, 219, 0.65), 0px 0px 60px 4px rgba(117, 111, 219, 0.2)'
            : '0px 0px 24px 0px rgba(117, 111, 219, 0.6)'
        }
        position="relative"
        transform={isFocused ? 'scale(1.015)' : 'scale(1)'}
        transition="box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), border 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
        w="100%"
      >
        {/* Inner background */}
        <Box
          _after={{
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            bg: isFocused ? 'whiteAlpha.200' : 'whiteAlpha.100',
            pointerEvents: 'none',
            transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          bg="indigo.800"
          borderRadius="full"
          position="relative"
        >
          <InputGroup size="lg">
            <InputLeftElement h="100%" pl={4} pointerEvents="none" w="auto">
              <GradientIcon
                gradientThemeKey="purple"
                height="28px"
                icon={MessageCircle}
                width="28px"
              />
            </InputLeftElement>

            <Input
              ref={inputRef}
              _focus={{ boxShadow: 'none' }}
              _placeholder={{
                color: isFocused ? 'whiteAlpha.600' : 'whiteAlpha.400',
                transition: 'color 0.3s ease',
              }}
              autoFocus={autoFocus}
              bg="transparent"
              border="none"
              borderRadius="full"
              color="white"
              fontSize="md"
              h="54px"
              pl="56px"
              placeholder='Try "What was my title success rate in Q3"'
              pr="60px"
              value={value}
              onBlur={() => setIsFocused(false)}
              onChange={(e) => setValue(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onKeyDown={handleKeyDown}
            />

            {/* Send button */}
            <Flex
              alignItems="center"
              h="100%"
              position="absolute"
              pr={3}
              right={0}
              zIndex={1}
            >
              <IconButton
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                }}
                _hover={{
                  bg: 'whiteAlpha.600',
                  transform: 'scale(1.08)',
                }}
                aria-label="Send message"
                bg={isFocused ? 'whiteAlpha.500' : 'whiteAlpha.400'}
                border="1px solid"
                borderColor={isFocused ? 'whiteAlpha.300' : 'whiteAlpha.200'}
                borderRadius="full"
                boxShadow={
                  isFocused
                    ? 'inset 0px 0px 8px 0px rgba(255, 255, 255, 0.35)'
                    : 'inset 0px 0px 5px 0px rgba(255, 255, 255, 0.25)'
                }
                color="white"
                h="40px"
                icon={<ArrowUp size={24} />}
                isDisabled={!value.trim()}
                minW="40px"
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                w="40px"
                onClick={handleSend}
              />
            </Flex>
          </InputGroup>
        </Box>
      </Box>
    </Flex>
  );
};
